import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Clock, Mic, ChevronRight, RotateCcw, PlayCircle } from 'lucide-react';
import MathText from './ui/MathText';
import AudioPlayer from './ui/AudioPlayer';
import QuizQuestionCard from './QuizQuestionCard';
import QuizSubmissionDetailsModal from './UserQuizSubmissionDetail';
import { useApiCaller } from '../utils/ApiUtilities';
import { getCustomQuizGetByIdUrl, getIeltsPerformanceUrl } from '../utils/UrlUtilities';
import { OPTIONFIELDTOCORRECTOPTIONMAP, WORD_BANK_TOPICS, PASSAGE_IMAGE_TOPICS } from '../utils/Constants';
import { loadInProgressAnswers, saveInProgressAnswers, clearInProgressAnswers, loadToeflProgress, saveToeflProgress, clearToeflProgress } from '../utils/BasicUtilities';
import useVoiceDictation from '../utils/useVoiceDictation';
import useAnswerAutosave from '../utils/useAnswerAutosave';
import AnsweredTracker from './ui/AnsweredTracker';
import HighlightableText from './ui/HighlightableText';
import ResizableSplit from './ui/ResizableSplit';
import { formatBdPst } from '../utils/DateUtilities';
import { ClickToViewImage } from './ImageViewer';

// ---------------------------------------------------------------------------
// IELTS model-test taker. Renders a Quiz whose metadata.test_kind === 'ielts_model_test'
// as the real exam: ordered sections → modules → tasks, an independent per-module clock, a
// pre-module instruction screen, audio (Listening, one clip per Part rendered once at the top),
// reading passages (rendered once at the top of each Passage module), and every IELTS question
// type — note/table/summary/sentence completion (TEXT), MCQ single + "choose TWO" (MCQ),
// TRUE/FALSE/NOT GIVEN & YES/NO/NOT GIVEN (MCQ), matching / word-bank / map-labelling (MATCHING,
// shared box rendered once), and Writing/Speaking prompts (recorded, unscored).
//
// Each sub-question is an ordinary 1-point question on the server, so scoring/statistics work
// unchanged. This assembles + submits answers keyed by question_id:
//   MCQ single -> "A"      MCQ multi ("choose TWO") -> "A,C"
//   MATCHING   -> "G" (box token; may exceed A–H, e.g. word banks A–J / roman i–x)
//   TEXT (completion / writing / speaking) -> text_answer "..."
// Reading (band-scored) and Listening (band-scored) report 0–9 bands; Writing/Speaking are unscored.
// ---------------------------------------------------------------------------

const DEFAULT_MODULE_MINUTES = 15;
const NAVBAR_OFFSET = '74px';

const TASK_LABELS = {
    completion: 'Complete the answer',
    mcq: 'Multiple choice',
    matching: 'Matching',
    writing: 'Writing',
    speaking: 'Speaking',
};

const fmt = (secs) => {
    const s = Math.max(0, secs | 0);
    const m = Math.floor(s / 60);
    return `${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};

// Custom audio player (green played-track) lives in ./ui/AudioPlayer, shared with the TOEFL taker.


function serializeAnswers(questionsById, answers) {
    const out = [];
    Object.keys(answers).forEach((qid) => {
        const q = questionsById[qid];
        const val = answers[qid];
        const entry = { question_id: Number(qid) };
        const qtype = q?.question_type;
        if (qtype === 'TEXT') {
            entry.text_answer = (val && val.text) || '';
        } else if (qtype === 'MATCHING') {
            // Box tokens are stored/submitted raw (not remapped through the option-letter map),
            // so word-bank (A–J) and roman-numeral boxes survive intact.
            entry.selected_option = Array.isArray(val) ? val.join(',') : (typeof val === 'string' ? val : '');
        } else if (Array.isArray(val)) {
            entry.selected_option = val.map((k) => OPTIONFIELDTOCORRECTOPTIONMAP[k]).filter(Boolean).sort().join(',');
        } else if (typeof val === 'string') {
            entry.selected_option = OPTIONFIELDTOCORRECTOPTIONMAP[val] || '';
        }
        out.push(entry);
    });
    return out;
}

// Render a completion template, replacing "(n)……" markers with inline inputs bound to the question
// whose metadata.ielts.no === n. Shared by notes/summary (NotesBlock) and table cells (TableBlock).
const MARKER_RE = /(\(\d+\)(?:\s*(?:……|…|\.{2,}))?)/g;
function renderInlineTemplate(text, byNo, answers, onText, keyPrefix) {
    const parts = String(text || '').split(MARKER_RE);
    return parts.map((part, idx) => {
        const m = part && part.match(/^\((\d+)\)/);
        if (!m) return <React.Fragment key={`${keyPrefix}-t${idx}`}>{part}</React.Fragment>;
        const no = m[1];
        const q = byNo[no];
        const val = (q && answers[q.question_id] && answers[q.question_id].text) || '';
        return (
            <span key={`${keyPrefix}-b${idx}`} style={{ display: 'inline-flex', alignItems: 'baseline', margin: '0 2px' }}>
                <sup style={{ color: '#6d28d9', fontWeight: 700, marginRight: 2 }}>{no}</sup>
                <input
                    value={val}
                    disabled={!q}
                    onChange={(e) => q && onText(q.question_id, e.target.value)}
                    style={{ width: '7rem', border: 'none', borderBottom: '2px solid #6d28d9', outline: 'none', textAlign: 'center', fontSize: '1rem', background: '#faf5ff' }}
                />
            </span>
        );
    });
}

const byNoMap = (questions) => {
    const m = {};
    questions.forEach((q) => { const no = q.metadata && q.metadata.ielts && q.metadata.ielts.no; if (no) m[no] = q; });
    return m;
};

// IELTS "Reading-Summary (Word Bank)": the shared word bank (A→word) is shown ONCE, read-only,
// above the summary blanks (which are typed — the taker enters the matching letter).
function WordBankBox({ bank }) {
    const letters = Object.keys(bank || {});
    if (letters.length === 0) return null;
    return (
        <div style={{ background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: '0.5rem', padding: '0.6rem 0.9rem', marginBottom: '0.75rem' }}>
            <div className="d-flex flex-wrap" style={{ gap: '0.35rem 1.5rem' }}>
                {letters.map((l) => (
                    <span key={l} style={{ color: '#4c1d95' }}>
                        <strong>{l}.</strong>{' '}<MathText text={String(bank[l])} />
                    </span>
                ))}
            </div>
        </div>
    );
}

// Notes / summary / sentence completion: the verbatim block with inline blanks.
function NotesBlock({ task, questions, answers, onText }) {
    const byNo = byNoMap(questions);
    return (
        <div className="kp-read-passage" style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '1rem', whiteSpace: 'pre-wrap', lineHeight: 2.1 }}>
            {renderInlineTemplate(task.template, byNo, answers, onText, `nb-${task.group_id || 't'}`)}
        </div>
    );
}

// Table completion: the verbatim table with inline blanks inside the cells.
function TableBlock({ task, questions, answers, onText }) {
    const byNo = byNoMap(questions);
    const tbl = task.table || { headers: [], rows: [] };
    return (
        <div className="kp-read-passage" style={{ overflowX: 'auto' }}>
            <table className="table table-bordered align-middle" style={{ background: '#fff' }}>
                {tbl.headers && tbl.headers.length > 0 && (
                    <thead className="table-light">
                        <tr>{tbl.headers.map((h, i) => <th key={i}>{h}</th>)}</tr>
                    </thead>
                )}
                <tbody>
                    {(tbl.rows || []).map((row, ri) => (
                        <tr key={ri}>
                            {row.map((cell, ci) => (
                                <td key={ci} style={{ whiteSpace: 'pre-wrap', verticalAlign: 'top' }}>
                                    {renderInlineTemplate(cell, byNo, answers, onText, `tb-${ri}-${ci}`)}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// Matching / word-bank / map-labelling: the shared option box (and optional map/diagram image) is
// rendered ONCE, then each item can be answered by DRAGGING a letter onto it OR using the dropdown.
function MatchingTask({ questions, answers, onSelect }) {
    const head = questions.find((q) => q.metadata && q.metadata.match_options) || questions[0];
    const box = (head && head.metadata && head.metadata.match_options) || {};
    const image = head && head.metadata && head.metadata.match_image;
    const letters = Object.keys(box);
    // Track which item row a letter is being dragged over so we can highlight the drop target.
    const [dragOverQid, setDragOverQid] = useState(null);
    const onDrop = (qid) => (e) => { e.preventDefault(); setDragOverQid(null); const l = e.dataTransfer.getData('text/plain'); if (l) onSelect(qid, l); };
    return (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: '0.5rem', overflow: 'hidden' }}>
            <div style={{ background: '#f5f3ff', padding: '0.75rem 1rem', borderBottom: '1px solid #e9d5ff' }}>
                <div className="small text-muted mb-2">Drag an option onto each item, or pick it from the dropdown.</div>
                {image && <ClickToViewImage src={image} containerStyle={{ textAlign: 'center' }} imgStyle={{ margin: '0 auto' }} />}
                <div className="d-flex flex-wrap gap-2">
                    {letters.map((l) => (
                        <div key={l} draggable onDragStart={(e) => e.dataTransfer.setData('text/plain', l)}
                            className="d-flex align-items-center gap-1"
                            style={{ cursor: 'grab', border: '1px solid #c4b5fd', background: '#fff', borderRadius: '0.375rem', padding: '0.25rem 0.6rem' }}>
                            <strong style={{ color: '#6d28d9' }}>{l}</strong><MathText text={String(box[l])} />
                        </div>
                    ))}
                </div>
            </div>
            <div style={{ padding: '0.5rem 1rem' }}>
                {questions.map((q, i) => {
                    const no = (q.metadata && q.metadata.ielts && q.metadata.ielts.no) || (i + 1);
                    const cur = (typeof answers[q.question_id] === 'string' ? answers[q.question_id] : '') || '';
                    const isOver = dragOverQid === q.question_id;
                    return (
                        <div key={q.question_id} className="d-flex align-items-center gap-2"
                            onDragOver={(e) => { e.preventDefault(); if (!isOver) setDragOverQid(q.question_id); }}
                            onDragEnter={(e) => { e.preventDefault(); setDragOverQid(q.question_id); }}
                            onDragLeave={(e) => { if (e.currentTarget === e.target) setDragOverQid(null); }}
                            onDrop={onDrop(q.question_id)}
                            style={{
                                padding: '0.5rem', borderRadius: '0.375rem',
                                border: isOver ? '2px dashed #6d28d9' : '2px dashed transparent',
                                background: isOver ? '#f5f3ff' : 'transparent',
                                borderBottom: !isOver && i < questions.length - 1 ? '1px solid #f3f4f6' : undefined,
                                transition: 'background 0.1s ease',
                            }}>
                            <span style={{ fontWeight: 600, color: '#4338ca', minWidth: '2.5rem' }}>{no}.</span>
                            <span className="kp-read-question" style={{ flex: 1 }}><MathText text={q.question_text} /></span>
                            <select
                                className="form-select fw-bold"
                                style={{ width: 'auto', minWidth: '7rem', border: '2px solid #6d28d9', background: '#f5f3ff', color: '#4338ca' }}
                                value={cur}
                                onChange={(e) => onSelect(q.question_id, e.target.value)}
                            >
                                <option value="">Select…</option>
                                {letters.map((l) => <option key={l} value={l}>{l}</option>)}
                            </select>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function SpeakingTask({ question, answer, onText, voice }) {
    const qid = question.question_id;
    const transcript = (answer && answer.text) || '';
    const listening = voice.listeningField === qid;
    return (
        <div>
            {question.audio_url && <AudioPlayer src={question.audio_url} />}
            <div className="d-flex gap-2 mb-2">
                <button
                    type="button"
                    onClick={() => voice.toggle(qid, transcript, (t) => onText(qid, t))}
                    disabled={!voice.supported}
                    className={`btn ${listening ? 'btn-danger' : 'btn-outline-primary'}`}
                >
                    <Mic size={16} style={{ marginRight: 6 }} />
                    {listening ? 'Stop recording' : (transcript ? 'Continue recording' : 'Record your response')}
                </button>
                {transcript && !listening && (
                    <button type="button" className="btn btn-outline-secondary" onClick={() => onText(qid, '')}>
                        <RotateCcw size={16} style={{ marginRight: 6 }} /> Re-answer
                    </button>
                )}
            </div>
            {!voice.supported && <div className="text-muted small">Speech capture needs Chrome or Edge.</div>}
            <div style={{ border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '0.75rem', minHeight: '3rem', background: '#f9fafb', color: '#374151', whiteSpace: 'pre-wrap' }}>
                {transcript || <span className="text-muted">Your transcript will appear here (not editable, not scored).</span>}
            </div>
        </div>
    );
}

export default function IeltsModelTest({ quizParam, onQuizEnd }) {
    const CallApi = useApiCaller();
    const voice = useVoiceDictation();
    const submissionId = quizParam?.submission_id;
    // Reuse the TOEFL progress store (module position + started flag) — it is keyed by submission id.
    const savedPos = loadToeflProgress(submissionId);
    const [questionsById, setQuestionsById] = useState({});
    const [blueprint, setBlueprint] = useState(null);
    const [answers, setAnswers] = useState(() => loadInProgressAnswers(submissionId));
    const [moduleIdx, setModuleIdx] = useState(savedPos?.moduleIdx || 0);
    const [started, setStarted] = useState(!!savedPos?.started);
    const [loading, setLoading] = useState(true);
    const [ended, setEnded] = useState(false);
    const [perf, setPerf] = useState(null);
    const [confirmLeave, setConfirmLeave] = useState(false);
    const [confirmQuit, setConfirmQuit] = useState(false);
    const [showSubmissionDetails, setShowSubmissionDetails] = useState(false);
    // Reader passage highlights, keyed per passage (section+module), kept for the whole session.
    const [highlightsByPassage, setHighlightsByPassage] = useState({});
    // For competitions, results stay locked until the competition window (quiz.end_time) closes.
    const [resultsLocked, setResultsLocked] = useState(false);
    const [windowEnd, setWindowEnd] = useState(null);
    const submittedRef = useRef(false);

    const modules = useMemo(() => {
        const out = [];
        (blueprint?.sections || []).forEach((sec) => {
            (sec.modules || []).forEach((m, mi) => {
                out.push({ sectionName: sec.name, sectionKey: sec.key, nav: sec.navigation, sectionModuleCount: (sec.modules || []).length, moduleNo: mi + 1, ...m });
            });
        });
        return out;
    }, [blueprint]);

    useEffect(() => {
        (async () => {
            try {
                const res = await CallApi({ url: getCustomQuizGetByIdUrl(quizParam.quiz_id), method: 'GET', token: true });
                if (res.success) {
                    const byId = {};
                    Object.values(res.data.questions || {}).forEach((arr) => arr.forEach((q) => { byId[q.question_id] = q; }));
                    setQuestionsById(byId);
                    setBlueprint(res.data.metadata || { sections: [] });
                    // Competition results (details/leaderboard) unlock only after the competition
                    // WINDOW (quiz.end_time) closes, not when this attempt ends.
                    const isComp = res.data.quiz_type === 'compitition';
                    const et = res.data.end_time;
                    setResultsLocked(isComp && (!et || new Date(et) > new Date()));
                    setWindowEnd(isComp ? et : null);
                }
            } finally {
                setLoading(false);
            }
        })();
    }, [CallApi, quizParam.quiz_id]);

    const setText = useCallback((qid, text) => setAnswers((a) => ({ ...a, [qid]: { text } })), []);
    useEffect(() => { saveInProgressAnswers(submissionId, answers); }, [submissionId, answers]);
    useEffect(() => { saveToeflProgress(submissionId, { moduleIdx, started }); }, [submissionId, moduleIdx, started]);
    // Server backstop: debounce-save answers so an abandoned attempt (browser closed before the
    // timer fires) can still be finalized+scored by the server's overdue sweep.
    const { scheduleSave, flush } = useAnswerAutosave(submissionId);
    useEffect(() => {
        if (submittedRef.current || Object.keys(answers).length === 0) return;
        scheduleSave(serializeAnswers(questionsById, answers));
    }, [answers, questionsById, scheduleSave]);
    const setOption = useCallback((qid, key) => setAnswers((a) => ({ ...a, [qid]: key })), []);
    const toggleMulti = useCallback((qid, key) => setAnswers((a) => {
        const cur = Array.isArray(a[qid]) ? a[qid] : [];
        return { ...a, [qid]: cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key] };
    }), []);

    const submit = useCallback(() => {
        if (submittedRef.current) return;
        submittedRef.current = true;
        setEnded(true);
        voice.stop();
        clearInProgressAnswers(submissionId);
        clearToeflProgress(submissionId);
        Promise.resolve(onQuizEnd({ ...quizParam, submitted_answers: serializeAnswers(questionsById, answers) }))
            .then(async () => {
                try {
                    const res = await CallApi({ url: getIeltsPerformanceUrl(quizParam.submission_id), method: 'GET', token: true });
                    if (res.success) setPerf(res.data);
                } catch (e) { /* performance is best-effort */ }
            });
    }, [answers, questionsById, onQuizEnd, quizParam, voice, CallApi, submissionId]);

    const current = modules[moduleIdx];
    const isLast = moduleIdx >= modules.length - 1;
    // Per-section module noun: Listening → Part, Reading → Passage, else Section.
    const sectionNoun = (key) => (key === 'listening' ? 'Part' : key === 'reading' ? 'Passage' : 'Section');
    const nextModule = modules[moduleIdx + 1];
    const nextLabel = nextModule ? `Next ${sectionNoun(nextModule.sectionKey)}` : 'Next';

    const goNext = useCallback(() => {
        voice.stop();
        flush(); // push the latest answers to the server before leaving this module
        if (isLast) submit();
        else { setModuleIdx((i) => i + 1); setStarted(false); }
    }, [isLast, submit, voice, flush]);

    // The countdown effect below intentionally does NOT depend on goNext (so a keystroke doesn't
    // reset the timer). Route expiry through a ref that always points at the latest goNext, so it
    // submits the answers the user actually entered instead of a stale (often empty) snapshot.
    const goNextRef = useRef(goNext);
    useEffect(() => { goNextRef.current = goNext; });

    const [secsLeft, setSecsLeft] = useState(0);
    useEffect(() => {
        if (!current || !started) return undefined;
        setSecsLeft((current.duration_minutes || DEFAULT_MODULE_MINUTES) * 60);
        const t = setInterval(() => {
            setSecsLeft((s) => {
                if (s <= 1) { clearInterval(t); goNextRef.current(); return 0; }
                return s - 1;
            });
        }, 1000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [moduleIdx, started, loading]);

    const moduleQuestionCount = (m) => (m.tasks || []).reduce((n, t) => n + (t.question_ids || []).length, 0);

    // The shared passage (Reading) / audio (Listening) for a module — rendered once at the top so
    // it isn't repeated per task. Taken from the first question that carries each.
    const moduleContext = (m) => {
        const qs = (m.tasks || []).flatMap((t) => (t.question_ids || []).map((id) => questionsById[id])).filter(Boolean);
        return {
            passage: (qs.find((q) => q.side_text) || {}).side_text || '',
            audio: (qs.find((q) => q.audio_url) || {}).audio_url || '',
        };
    };

    const renderTask = (task) => {
        const qs = (task.question_ids || []).map((id) => questionsById[id]).filter(Boolean);
        if (qs.length === 0) return null;
        // Completion tasks render the verbatim notes/table block with inline blanks. Key off the
        // presence of a template/table too (not just `layout`), so a blueprint that carries the
        // block but omits the `layout` flag still renders as inline blanks rather than plain cards.
        if (task.layout === 'notes' || task.template) return <NotesBlock task={task} questions={qs} answers={answers} onText={setText} />;
        if (task.layout === 'table' || task.table) return <TableBlock task={task} questions={qs} answers={answers} onText={setText} />;
        if (task.task === 'matching' || qs.every((q) => q.question_type === 'MATCHING')) {
            return <MatchingTask questions={qs} answers={answers} onSelect={setOption} />;
        }
        const isSpeaking = qs.some((q) => q.metadata && q.metadata.ielts && q.metadata.ielts.capture === 'speaking');
        if (isSpeaking) {
            return qs.map((q) => (
                <div key={q.question_id} style={{ marginBottom: '1rem', padding: '1rem', border: '1px solid #e5e7eb', borderRadius: '0.5rem', background: '#fff' }}>
                    <p className="kp-read-question"><MathText text={q.question_text} /></p>
                    <SpeakingTask question={q} answer={answers[q.question_id]} onText={setText} voice={voice} />
                </div>
            ));
        }
        // MCQ single / choose-two, TRUE-FALSE-NOT GIVEN, and Writing route through the shared card.
        // The passage/audio are already in the module header (never repeated). The canonical paper
        // number rides on metadata.ielts.no; Writing prompts get a taller box and no "Qn." label.
        const isWriting = qs.some((q) => q.subject === 'Writing');
        return qs.map((q, i) => (
            <QuizQuestionCard key={q.question_id} question={q} qIndex={i}
                displayNumber={q.metadata && q.metadata.ielts ? q.metadata.ielts.no : undefined}
                textareaRows={isWriting ? 10 : 3}
                includePassage={false}
                answer={answers[q.question_id]} onOptionSelect={setOption} onMultiToggle={toggleMulti}
                onTextChange={setText} onBlankSelect={() => {}} />
        ));
    };

    if (loading) return <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }}><div className="modal-dialog modal-dialog-centered"><div className="modal-content p-4 text-center">Loading model test…</div></div></div>;

    if (ended) {
        return (
            <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content p-4">
                        <h4 className="fw-bold mb-2 text-center">Test submitted ✅</h4>
                        {perf ? (
                            <>
                                <div className="text-center mb-3">
                                    <span className="badge bg-primary fs-6 px-3 py-2">Overall band: {perf.total_band != null ? perf.total_band.toFixed(1) : '—'} / 9.0</span>
                                    <div className="text-muted small mt-1">Overall band is the average of the scored sections (rounded to the nearest half-band).</div>
                                </div>
                                <table className="table table-sm align-middle">
                                    <thead><tr><th>Section</th><th className="text-center">Raw</th><th className="text-center">Band</th></tr></thead>
                                    <tbody>
                                        {perf.sections.map((s) => (
                                            <tr key={s.name}>
                                                <td>{s.name}</td>
                                                <td className="text-center">{s.unscored ? '—' : `${s.raw}/${s.max}`}</td>
                                                <td className="text-center">{s.unscored ? 'Not scored' : (s.band != null ? s.band.toFixed(1) : '—')}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot><tr className="fw-bold"><td>Overall</td><td className="text-center">{perf.total_raw}/{perf.total_max}</td><td className="text-center">{perf.total_band != null ? perf.total_band.toFixed(1) : '—'}</td></tr></tfoot>
                                </table>
                                <p className="text-muted small text-center">Listening &amp; Reading are band-scored from the official raw→band tables. Writing and Speaking are recorded but not scored.</p>
                            </>
                        ) : (
                            <p className="text-muted text-center">Listening &amp; Reading are scored; Writing and Speaking are recorded but not scored.</p>
                        )}
                        {resultsLocked ? (
                            <p className="text-muted small text-center mb-2">
                                Your answers are submitted. Full submission details &amp; the leaderboard open
                                after the competition ends{windowEnd ? <> — <strong>{formatBdPst(windowEnd)}</strong></> : null}.
                            </p>
                        ) : (
                            <button className="btn btn-primary w-100 mb-2" onClick={() => setShowSubmissionDetails(true)}>Show Submission Details</button>
                        )}
                        <button className="btn btn-primary w-100" onClick={() => onQuizEnd(null)}>Close</button>
                    </div>
                </div>
                {showSubmissionDetails && (
                    <QuizSubmissionDetailsModal submissionId={submissionId} onClose={() => setShowSubmissionDetails(false)} />
                )}
            </div>
        );
    }
    if (!current) return null;

    if (!started) {
        const firstOfSection = moduleIdx === 0 || modules[moduleIdx - 1].sectionKey !== current.sectionKey;
        const sectionModules = modules.filter((m) => m.sectionKey === current.sectionKey);
        const sectionQs = sectionModules.reduce((n, m) => n + moduleQuestionCount(m), 0);
        const taskTypes = [...new Set((current.tasks || []).map((t) => TASK_LABELS[t.task] || t.task))];
        return (
            <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.6)' }} tabIndex="-1">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content p-4">
                        {firstOfSection && (
                            <div className="alert alert-primary">
                                <div className="fw-bold fs-5 mb-1">{current.sectionName} Section</div>
                                <div>{sectionModules.length} module{sectionModules.length > 1 ? 's' : ''} · {sectionQs} question{sectionQs > 1 ? 's' : ''} total.</div>
                                <div className="small mt-1">You cannot return to a module once you move on. Each module is separately timed.</div>
                            </div>
                        )}
                        <h5 className="fw-bold text-dark">{current.sectionName} — {current.name}</h5>
                        <ul className="text-muted">
                            <li>{moduleQuestionCount(current)} question{moduleQuestionCount(current) > 1 ? 's' : ''}</li>
                            <li>Task type{taskTypes.length > 1 ? 's' : ''}: {taskTypes.join(', ')}</li>
                            <li>Time: {current.duration_minutes || DEFAULT_MODULE_MINUTES} minute{(current.duration_minutes || DEFAULT_MODULE_MINUTES) > 1 ? 's' : ''} (the clock starts when you begin)</li>
                        </ul>
                        <button className="btn btn-primary w-100 mt-2" onClick={() => setStarted(true)}>
                            <PlayCircle size={18} style={{ marginRight: 6 }} /> Begin {current.name}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const ctx = moduleContext(current);
    // Ordered questions of the current module + whether each is answered → bottom progress boxes.
    const moduleQuestions = (current.tasks || []).flatMap((t) => (t.question_ids || []).map((id) => questionsById[id])).filter(Boolean);
    const isAnswered = (q) => {
        const v = answers[q.question_id];
        if (v == null) return false;
        if (typeof v === 'string') return v.trim() !== '';
        if (Array.isArray(v)) return v.length > 0;
        if (typeof v === 'object') return !!(v.text && v.text.trim());
        return false;
    };
    // The shared word bank / passage image for a task (from the passage group, surfaced onto the
    // member questions' metadata). Shown once above the blanks for their topic.
    const wordBankFor = (task) => {
        const qs = (task.question_ids || []).map((id) => questionsById[id]).filter(Boolean);
        const q = qs.find((x) => WORD_BANK_TOPICS.includes(x.topic) && x.metadata && x.metadata.word_bank);
        return q ? q.metadata.word_bank : null;
    };
    const passageImageFor = (task) => {
        const qs = (task.question_ids || []).map((id) => questionsById[id]).filter(Boolean);
        const q = qs.find((x) => PASSAGE_IMAGE_TOPICS.includes(x.topic) && x.metadata && x.metadata.passage_image);
        return q ? q.metadata.passage_image : null;
    };
    const taskBlocks = (current.tasks || []).map((task, ti) => (
        <div key={ti} style={{ marginBottom: '1.5rem' }}>
            {task.title && <h5 className="fw-bold text-dark mb-1">{task.title}</h5>}
            {task.instructions && <p className="text-muted">{task.instructions}</p>}
            {(() => { const img = passageImageFor(task); return img ? <div style={{ textAlign: 'center', marginBottom: '0.75rem' }}><ClickToViewImage src={img} imgStyle={{ maxWidth: '100%', margin: '0 auto' }} /></div> : null; })()}
            {(() => { const wb = wordBankFor(task); return wb ? <WordBankBox bank={wb} /> : null; })()}
            {renderTask(task)}
        </div>
    ));
    return (
        <>
        <div style={{ position: 'fixed', top: NAVBAR_OFFSET, left: 0, right: 0, bottom: 0, zIndex: 900, display: 'flex', flexDirection: 'column', background: '#f8fafc' }}>
            <div className="d-flex justify-content-between align-items-center" style={{ background: 'linear-gradient(145deg,#6d28d9,#4338ca)', color: '#fff', padding: '0.75rem 1.25rem', flexShrink: 0, boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
                <div>
                    <div className="fw-bold fs-5">{current.sectionName} — {current.name}</div>
                    <div className="small" style={{ opacity: 0.85 }}>Module {moduleIdx + 1} of {modules.length}</div>
                </div>
                <div className={`d-flex align-items-center gap-2 fs-5 fw-bold${secsLeft <= 60 ? ' kp-timer-flash' : ''}`} style={{ backgroundColor: '#ef4444', color: '#ffffff', padding: '0.3rem 0.8rem', borderRadius: '0.5rem' }} title="Time left in this module">
                    <Clock size={18} /> <span className="small fw-normal">Time left</span> {fmt(secsLeft)}
                </div>
            </div>
            {ctx.passage ? (
                // Reading (passage present): passage on the LEFT, questions on the RIGHT — each
                // scrolls independently, with a draggable divider to resize. Applies wherever this
                // taker renders (published / competition / micro).
                (() => {
                    const pKey = `${current.sectionKey}-${current.moduleNo}`;
                    return (
                        <ResizableSplit
                            style={{ flex: 1, minHeight: 0, padding: '1.25rem' }}
                            gap="1.25rem"
                            initialLeftPct={50}
                            left={
                                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '1rem' }}>
                                    {ctx.audio && <AudioPlayer src={ctx.audio} />}
                                    <HighlightableText
                                        className="kp-read-passage"
                                        text={ctx.passage}
                                        highlights={highlightsByPassage[pKey] || []}
                                        onChange={(next) => setHighlightsByPassage(prev => ({ ...prev, [pKey]: next }))}
                                    />
                                </div>
                            }
                            right={taskBlocks}
                        />
                    );
                })()
            ) : (
                // No passage (Listening / Writing / Speaking): single scrollable column, audio on top.
                <div style={{ flex: 1, overflowY: 'auto', padding: '1.25rem' }}>
                    {ctx.audio && <AudioPlayer src={ctx.audio} />}
                    {taskBlocks}
                </div>
            )}
            {/* Question progress: one box per question in this module, filled when answered. */}
            {moduleQuestions.length > 0 && (
                <AnsweredTracker
                    questions={moduleQuestions}
                    isAnswered={isAnswered}
                    numberByQid={Object.fromEntries(moduleQuestions.map((q, i) => [q.question_id, (q.metadata && q.metadata.ielts && q.metadata.ielts.no) || (i + 1)]))}
                    style={{ flexShrink: 0 }}
                />
            )}
            <div className="d-flex justify-content-between" style={{ padding: '0.75rem 1.25rem', borderTop: '1px solid #e5e7eb', background: '#fff', flexShrink: 0 }}>
                <button className="btn btn-outline-secondary" onClick={() => setConfirmQuit(true)}>Quit &amp; submit</button>
                <button className="btn btn-primary" onClick={() => setConfirmLeave(true)}>
                    {isLast ? 'Finish test' : nextLabel} <ChevronRight size={16} />
                </button>
            </div>
        </div>
        {confirmLeave && (
            <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.7)', zIndex: 1060 }} tabIndex="-1">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content p-4 text-center">
                        <div style={{ fontSize: '2rem' }}>⚠️</div>
                        <h5 className="fw-bold mb-2">This closes {current.name}</h5>
                        <p className="text-muted mb-3">
                            You will <strong>not be able to return</strong> to {current.sectionName} — {current.name} once you continue.
                        </p>
                        <div className="d-flex gap-2 justify-content-center">
                            <button className="btn btn-outline-secondary" onClick={() => setConfirmLeave(false)}>Stay on this module</button>
                            <button className="btn btn-primary" onClick={() => { setConfirmLeave(false); goNext(); }}>
                                {isLast ? 'Finish test' : `Continue to next ${sectionNoun((nextModule || current).sectionKey).toLowerCase()}`}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )}
        {confirmQuit && (
            <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.7)', zIndex: 1060 }} tabIndex="-1">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content p-4 text-center">
                        <div style={{ fontSize: '2rem' }}>🚪</div>
                        <h5 className="fw-bold mb-2">Quit the test?</h5>
                        <p className="text-muted mb-3">
                            Your progress will be <strong>submitted as-is</strong> and the test will end.
                            Unanswered questions stay blank. This can't be undone.
                        </p>
                        <div className="d-flex gap-2 justify-content-center">
                            <button className="btn btn-outline-secondary" onClick={() => setConfirmQuit(false)}>Keep taking the test</button>
                            <button className="btn btn-danger" onClick={() => { setConfirmQuit(false); submit(); }}>Quit &amp; submit</button>
                        </div>
                    </div>
                </div>
            </div>
        )}
        </>
    );
}
